<script setup>
/**
 * Steps 1 to 3 of selling a flight, in one dialog: record the customer's brief, search on
 * it, and send a shortlist the customer can tap.
 *
 * Preferred time sorts the results, it never filters them. Hiding inventory an agent did
 * not ask to hide is the same mistake the content-source notice on the search screen
 * exists to prevent.
 *
 * Language is stored on the brief and the outbound message is written in it, so recording
 * it is not decoration.
 *
 * Prices stay as the integer minor units Travelport sent, right up to the moment they are
 * displayed. Nothing here turns a fare into a float.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import TravelSearchAPI from 'dashboard/api/travel/search';
import TravelQuotesAPI from 'dashboard/api/travel/quotes';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  // The brief a previous shortlist was built from, when an agent reopens one from the
  // conversation. The OFFERS are deliberately not carried over: a provider offer id is
  // scoped to the search response that produced it, so re-sending a stored one would quote
  // the customer a fare that may no longer exist. The brief is re-searched instead.
  initialBrief: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const { enabledLanguages } = useConfig();

// WhatsApp caps an interactive list at ten rows, which is the tightest of the three
// channels this can go out on.
// Five, matching the API ceiling. A shortlist is read on a phone, usually in WhatsApp, and
// a customer scrolling past five fares is choosing badly rather than choosing from more.
// The search results below may be longer - only five can be sent in one shortlist.
const MAX_OPTIONS = 5;

// Local departure hour ranges, used only for ordering.
const TIME_WINDOWS = {
  morning: [0, 12],
  afternoon: [12, 17],
  evening: [17, 24],
};

const SOURCE_STYLES = {
  gds: 'bg-n-slate-3 text-n-slate-12',
  ndc: 'bg-n-teal-3 text-n-teal-11',
  merged: 'bg-n-amber-3 text-n-amber-11',
};

const dialogRef = ref(null);
const brief = ref({
  origin: props.initialBrief?.origin || '',
  destination: props.initialBrief?.destination || '',
  departureDate: props.initialBrief?.departure_date || '',
  returnDate: props.initialBrief?.return_date || '',
  passengers: props.initialBrief?.passengers?.[0]?.count || 1,
  preferredTime: props.initialBrief?.preferred_time || '',
  language: props.initialBrief?.language || 'en',
  notes: props.initialBrief?.notes || '',
});
const offers = ref([]);
const selectedKeys = ref([]);
const isSearching = ref(false);
const isSending = ref(false);
const hasSearched = ref(false);

onMounted(() => {
  dialogRef.value?.open();
});

const languageOptions = computed(() =>
  (enabledLanguages ?? []).map(language => ({
    value: language.iso_639_1_code,
    label: language.name,
  }))
);

// Empty is "any time", which is a real answer rather than an absent one - the search runs
// unsorted, it does not refuse.
const timeWindowOptions = computed(() =>
  ['ANY', 'MORNING', 'AFTERNOON', 'EVENING'].map(window => ({
    value: window === 'ANY' ? '' : window.toLowerCase(),
    label: t(`TRAVEL.SEND_OPTIONS.TIME_WINDOWS.${window}`),
  }))
);

const offerKey = offer =>
  `${offer.id}-${offer.sequence}-${offer.source}-${offer.brand_name}`;

const canSearch = computed(
  () =>
    brief.value.origin.trim().length === 3 &&
    brief.value.destination.trim().length === 3 &&
    !!brief.value.departureDate &&
    !isSearching.value
);

const departureHour = offer => {
  const departureTime = offer.flights?.[0]?.departure_time;
  if (!departureTime) return null;
  const [, time] = String(departureTime).split('T');
  return time ? parseInt(time.slice(0, 2), 10) : null;
};

const matchesPreferredTime = offer => {
  const window = TIME_WINDOWS[brief.value.preferredTime];
  const hour = departureHour(offer);
  if (!window || hour === null) return false;
  return hour >= window[0] && hour < window[1];
};

// Preferred matches first, original provider order preserved within each group.
const sortedOffers = computed(() => {
  if (!brief.value.preferredTime) return offers.value;
  return [
    ...offers.value.filter(matchesPreferredTime),
    ...offers.value.filter(offer => !matchesPreferredTime(offer)),
  ];
});

const selectedOffers = computed(() =>
  offers.value.filter(offer => selectedKeys.value.includes(offerKey(offer)))
);

const canSend = computed(
  () =>
    selectedOffers.value.length > 0 &&
    selectedOffers.value.length <= MAX_OPTIONS &&
    !isSending.value
);

const formatPrice = price => {
  if (!price) return '—';
  const value = price.amount / 10 ** price.decimal_place;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: price.currency,
    minimumFractionDigits: price.decimal_place,
  }).format(value);
};

const itineraryLabel = offer =>
  offer.flights.map(flight => `${flight.carrier}${flight.number}`).join(' · ');

const toggleOffer = offer => {
  const key = offerKey(offer);
  if (selectedKeys.value.includes(key)) {
    selectedKeys.value = selectedKeys.value.filter(item => item !== key);
  } else if (selectedKeys.value.length < MAX_OPTIONS) {
    selectedKeys.value = [...selectedKeys.value, key];
  }
};

const requirementsPayload = () => ({
  origin: brief.value.origin.trim().toUpperCase(),
  destination: brief.value.destination.trim().toUpperCase(),
  departure_date: brief.value.departureDate,
  return_date: brief.value.returnDate || undefined,
  preferred_time: brief.value.preferredTime || undefined,
  language: brief.value.language,
  notes: brief.value.notes || undefined,
  passengers: [{ type: 'ADT', count: Number(brief.value.passengers) }],
});

const search = async () => {
  isSearching.value = true;
  hasSearched.value = true;
  selectedKeys.value = [];
  try {
    const { data } = await TravelSearchAPI.search(requirementsPayload());
    offers.value = data.offers;
  } catch (error) {
    offers.value = [];
    useAlert(
      error?.response?.data?.error || t('TRAVEL.SEND_OPTIONS.SEARCH_ERROR')
    );
  } finally {
    isSearching.value = false;
  }
};

const onSubmit = async () => {
  if (!canSend.value) return;

  isSending.value = true;
  try {
    await TravelQuotesAPI.create({
      conversation_id: props.conversationId,
      requirements: requirementsPayload(),
      offers: selectedOffers.value,
    });
    useAlert(t('TRAVEL.SEND_OPTIONS.SUCCESS'));
    emit('close');
  } catch (error) {
    useAlert(error?.response?.data?.error || t('TRAVEL.SEND_OPTIONS.ERROR'));
  } finally {
    isSending.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="xl"
    overflow-y-auto
    :title="$t('TRAVEL.SEND_OPTIONS.TITLE')"
    :description="$t('TRAVEL.SEND_OPTIONS.DESC')"
    :confirm-button-label="
      $t('TRAVEL.SEND_OPTIONS.SUBMIT', { count: selectedOffers.length })
    "
    :cancel-button-label="$t('TRAVEL.SEND_OPTIONS.CANCEL')"
    :disable-confirm-button="!canSend"
    :is-loading="isSending"
    @confirm="onSubmit"
    @close="emit('close')"
  >
    <div class="flex flex-col w-full gap-4">
      <form
        class="grid grid-cols-1 gap-3 sm:grid-cols-3"
        @submit.prevent="search"
      >
        <Input
          v-model="brief.origin"
          :label="$t('TRAVEL.SEND_OPTIONS.ORIGIN')"
          :placeholder="$t('TRAVEL.FLIGHT_SEARCH.FORM.ORIGIN_PLACEHOLDER')"
          maxlength="3"
        />
        <Input
          v-model="brief.destination"
          :label="$t('TRAVEL.SEND_OPTIONS.DESTINATION')"
          :placeholder="$t('TRAVEL.FLIGHT_SEARCH.FORM.DESTINATION_PLACEHOLDER')"
          maxlength="3"
        />
        <Input
          v-model="brief.departureDate"
          type="date"
          :label="$t('TRAVEL.SEND_OPTIONS.DEPART')"
        />
        <Input
          v-model="brief.returnDate"
          type="date"
          :label="$t('TRAVEL.SEND_OPTIONS.RETURN')"
        />
        <Input
          v-model="brief.passengers"
          type="number"
          min="1"
          max="9"
          :label="$t('TRAVEL.SEND_OPTIONS.PASSENGERS')"
        />
        <label
          class="flex flex-col gap-1 mb-0 text-sm font-medium text-n-slate-12"
        >
          {{ $t('TRAVEL.SEND_OPTIONS.PREFERRED_TIME') }}
          <Select
            v-model="brief.preferredTime"
            :options="timeWindowOptions"
            class="!w-full [&>select]:w-full"
          />
        </label>
        <label
          class="flex flex-col gap-1 mb-0 text-sm font-medium text-n-slate-12"
        >
          {{ $t('TRAVEL.SEND_OPTIONS.LANGUAGE') }}
          <Select
            v-model="brief.language"
            :options="languageOptions"
            class="!w-full [&>select]:w-full"
          />
        </label>
        <Input
          v-model="brief.notes"
          class="sm:col-span-3"
          :label="$t('TRAVEL.SEND_OPTIONS.NOTES')"
          :placeholder="$t('TRAVEL.SEND_OPTIONS.NOTES_PLACEHOLDER')"
        />
        <!-- On its own row rather than in the third grid column, where it sat level with
             "Other preferences" and read as that field's action. -->
        <div class="flex justify-end sm:col-span-3">
          <NextButton
            type="submit"
            :label="$t('TRAVEL.SEND_OPTIONS.SEARCH')"
            :is-loading="isSearching"
            :disabled="!canSearch"
          />
        </div>
      </form>

      <div v-if="sortedOffers.length" class="flex flex-col gap-2">
        <p class="text-xs text-n-slate-11">
          {{ $t('TRAVEL.SEND_OPTIONS.PICK_HINT', { max: MAX_OPTIONS }) }}
        </p>
        <label
          v-for="offer in sortedOffers"
          :key="offerKey(offer)"
          class="flex items-center gap-3 px-3 py-2 border rounded-lg cursor-pointer border-n-weak"
        >
          <input
            type="checkbox"
            :checked="selectedKeys.includes(offerKey(offer))"
            @change="toggleOffer(offer)"
          />
          <span
            class="px-2 py-0.5 text-xs font-medium uppercase rounded-full shrink-0"
            :class="SOURCE_STYLES[offer.source]"
          >
            {{ $t(`TRAVEL.QUOTE.SOURCE.${offer.source.toUpperCase()}`) }}
          </span>
          <span class="flex-1 text-sm truncate text-n-slate-12">
            {{ itineraryLabel(offer) }}
          </span>
          <span class="text-sm font-medium text-n-slate-12 shrink-0">
            {{ formatPrice(offer.total_price) }}
          </span>
        </label>
      </div>

      <p
        v-else-if="hasSearched && !isSearching"
        class="text-sm text-center text-n-slate-11"
      >
        {{ $t('TRAVEL.FLIGHT_SEARCH.EMPTY') }}
      </p>
    </div>
  </Dialog>
</template>
